import React, { useContext, useId, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import TextButton from '@wix/da-ds/pkg/Buttons/TextButton';
import TagList from '@wix/da-ds/pkg/TagList';
import { LegalDocumentType } from '@wix/da-shared-react/pkg/redux/uploads/types';
import ButtonFieldLayout from '../../layouts/common/ButtonFieldLayout';
import LegalDocumentsPopup from '../../modals/LegalDocumentsPopup';
import { SubmitFormContext } from '../../context';

import s from './LegalDocumentsField.scss';

interface Props {
  className?: string;
}

const LegalDocumentsField: React.FC<Props> = ({ className }) => {
  const { t } = useTranslation();
  const { initResponse } = useContext(SubmitFormContext)!;
  const [showPopup, togglePopup] = useState(false);
  const id = useId();
  const [narfDocumentUrl, setNarfDocumentUrl] = useState(
    initResponse?.narfDocumentUrl
  );
  const [sprfDocumentUrl, setSprfDocumentUrl] = useState(
    initResponse?.sprfDocumentUrl
  );
  const hasLegalDocuments = useMemo(
    () => narfDocumentUrl || sprfDocumentUrl,
    [narfDocumentUrl, sprfDocumentUrl]
  );
  const deviationId = initResponse?.deviation?.deviationId;

  const handleUploadComplete = (
    documentType: LegalDocumentType,
    documentUrl: string
  ) => {
    if (documentType === 'narf') {
      setNarfDocumentUrl(documentUrl);
    } else if (documentType === 'sprf') {
      setSprfDocumentUrl(documentUrl);
    }
  };

  const uploadedDocsTags = hasLegalDocuments && (
    <TagList
      className={s['tags']}
      tags={[
        sprfDocumentUrl
          ? { tagname: t('deviationSubmit.form.legalDocuments.tags.sprf') }
          : null,
        narfDocumentUrl
          ? { tagname: t('deviationSubmit.form.legalDocuments.tags.narf') }
          : null,
      ]}
      size="small"
    />
  );

  return (
    <div className={className}>
      <ButtonFieldLayout
        label={t('deviationSubmit.form.legalDocuments.label')}
        labelProps={{ id: `${id}-label`, htmlFor: id }}
        bottomSlot={uploadedDocsTags}
      >
        <TextButton
          variant="main"
          size="small"
          onClick={() => togglePopup(true)}
        >
          {hasLegalDocuments ? t('common.edit') : t('common.add')}
        </TextButton>
      </ButtonFieldLayout>

      {showPopup && (
        <LegalDocumentsPopup
          deviationId={deviationId!}
          narfDocumentUrl={narfDocumentUrl}
          sprfDocumentUrl={sprfDocumentUrl}
          requestClose={() => togglePopup(false)}
          onUploadComplete={handleUploadComplete}
        />
      )}
    </div>
  );
};
LegalDocumentsField.displayName = 'LegalDocumentsField';

export default LegalDocumentsField;
